import { DataValidator, IsBoolean, IsNumber, IsString, Required, transformKeys, ValidateNested } from "@/utils/type-vaildator";
import api from "./api";

interface VideoAudioVideoProcessBatchParams {
  tasks: {
    text: string;
    voice_id: string;
    voice_model_name: string;
    scene_id: string;
    voice_settings: {
      stability: number;
      similarity_boost: number;
      style_exaggeration: number;
      speed: number;
    };
    audio_save_to_db: boolean;
    audio_name: string;
    audio_upload_to_s3: boolean;
    level: string;
    custom_extra: {
      project_name: string;
    };
    callback_url: string;
    effect_extra: Record<string, any>;
    folder_id: string;
    project_id: string;
    actor_id: string;
    video_name: string;
    video_desc: string;
  }[];
}

interface VideoAudioVideoProcessBatchProps {
  tasks: {
    text: string;
    voiceId: string;
    voiceModelName: string;
    sceneId: string;
    voiceSettings: {
      stability: number;
      similarityBoost: number;
      styleExaggeration: number;
      speed: number;
    };
    folderId: string;
    projectId: string;
    actorId: string;
  }[];
}

interface VideoAudioVideoProcessBatchResponse {
  current_count: number;
  current_page: number;
  page_size: number;
  total: number;
  videos: {
    actor: {
      actor_model_id: string;
      age_group: string;
      compressed_actor_default_video_url: string;
      full_actor_default_video_url: string;
      gender: string;
      id: string;
      image_url: string;
      is_exclusive_pro: boolean;
      is_favorite: boolean;
      is_free_speech: boolean;
      is_hd: boolean;
      is_my_actor: boolean;
      is_pro: boolean;
      name: string;
      situation: string;
      sort_order: number;
      tags: null;
      use_count: number;
    };
    compressed_video_url: string;
    created_at: string;
    id: string;
    input_text: string;
    process_result: string;
    task_id: string;
    updated_at: string;
    video_desc: string;
    video_name: string;
    video_status: number;
    video_url: string;
    voice: {
      age_group: string;
      audio_url: string;
      duration: number;
      emotion: string;
      gender: string;
      id: string;
      is_selected: boolean;
      is_starred: boolean;
      language: string;
      model_name: string;
      name: string;
      script: string;
      sort_order: number;
      speed: number;
      type: number;
      use_count: number;
      video_id: string;
      voice_tts_id: string;
    };
  }[] | null;
}

// Actor 嵌套类
class Actor {
  @Required("Actor model ID cannot be empty")
  @IsString("Actor model ID must be a string")
  actorModelId: string;

  @Required("ID cannot be empty")
  @IsString("ID must be a string")
  id: string;

  @Required("Image URL cannot be empty")
  @IsString("Image URL must be a string")
  imageUrl: string;

  @Required("Name cannot be empty")
  @IsString("Name must be a string")
  name: string;
}

// Voice 嵌套类
class Voice {
  @Required("Age group cannot be empty")
  @IsString("Age group must be a string")
  ageGroup: string;

  @Required("Audio URL cannot be empty")
  @IsString("Audio URL must be a string")
  audioUrl: string;

  // @Required("Emotion cannot be empty")
  // @IsString("Emotion must be a string")
  // emotion: string;

  @Required("Gender cannot be empty")
  @IsString("Gender must be a string")
  gender: string;

  @Required("ID cannot be empty")
  @IsString("ID must be a string")
  id: string;

  @Required("Is starred cannot be empty")
  @IsBoolean("Is starred must be a boolean")
  isStarred: boolean;

  @Required("Language cannot be empty")
  @IsString("Language must be a string")
  language: string;

  @Required("Model name cannot be empty")
  @IsString("Model name must be a string")
  modelName: string;

  @Required("Name cannot be empty")
  @IsString("Name must be a string")
  name: string;

  @Required("Speed cannot be empty")
  @IsNumber("Speed must be a number")
  speed: number;

  @Required("Voice TTS ID cannot be empty")
  @IsString("Voice TTS ID must be a string")
  voiceTtsId: string;
}

// Video 嵌套类
class Video {
  @Required("Actor cannot be empty")
  @ValidateNested(Actor)
  actor: Actor;

  @IsString("Compressed video URL must be a string")
  compressedVideoUrl: string;

  @Required("Created at cannot be empty")
  @IsString("Created at must be a string")
  createdAt: string;

  @Required("ID cannot be empty")
  @IsString("ID must be a string")
  id: string;

  @Required("Input text cannot be empty")
  @IsString("Input text must be a string")
  inputText: string;

  taskId?: string;

  @Required("Video status cannot be empty")
  @IsNumber("Video status must be a number")
  videoStatus: number;

  @IsString("Video URL must be a string")
  videoUrl: string;

  @Required("Voice cannot be empty")
  @ValidateNested(Voice)
  voice: Voice;
}

// 主数据类
export class VideoAudioVideoProcessBatchData {
  @Required("Total cannot be empty")
  @IsNumber("Total must be a number")
  total: number;

  videos: Video[] | null;
}

const transformAndValidateVideoVideosByFolderProject = (
  response: VideoAudioVideoProcessBatchResponse
): VideoAudioVideoProcessBatchData => {
  const transformedData = transformKeys(response);

  try {
    const validatedData = DataValidator.validateOrThrow(transformedData, VideoAudioVideoProcessBatchData);
    
    // 手动验证 videos 数组
    if (transformedData.videos !== null) {
      if (!Array.isArray(transformedData.videos)) {
        throw new Error("Videos must be an array or null");
      }
      
      // 验证数组中的每个 Video 对象
      for (const video of transformedData.videos) {
        DataValidator.validateOrThrow(video, Video);
      };
    }
    
    return validatedData;
  } catch (error) {
    console.error("Video videos by folder project data validation failed:", error.message);
    throw error;
  }
};

const URL = "/video/audio-video/process-batch";

export const videoAudioVideoProcessBatch = async (
  props: VideoAudioVideoProcessBatchProps
) => {
  const params: VideoAudioVideoProcessBatchParams = {
    tasks: props.tasks.map((task) => ({
      text: task.text,
      voice_id: task.voiceId,
      voice_model_name: task.voiceModelName,
      scene_id: task.sceneId,
      voice_settings: {
        stability: task.voiceSettings.stability,
        similarity_boost: task.voiceSettings.similarityBoost,
        style_exaggeration: task.voiceSettings.styleExaggeration,
        speed: task.voiceSettings.speed,
      },
      folder_id: task.folderId,
      project_id: task.projectId,
      actor_id: task.actorId,
      // 默认值
      audio_save_to_db: true,
      audio_name: "",
      audio_upload_to_s3: true,
      level: "high",
      custom_extra: { project_name: "" },
      callback_url: "https://api.bizdesk.ai/api/v1/video/digital-human/callback",
      effect_extra: {},
      video_name: "",
      video_desc: "",
    })),
  };
  const res = await api.post<VideoAudioVideoProcessBatchResponse>(URL, params, {
    // 5分钟 避免触发超时
    timeout: 1000 * 60 * 5,
  });
  const validatedData = transformAndValidateVideoVideosByFolderProject(res.data);
  return validatedData;
};
